import React, { useCallback, useMemo, useState } from 'react';
import { Container, Grid, Paper, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router';

import { usePromise } from '@paragon/client-tools';
import { LumberListPrototype, LumberSet } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';

import { upsertLumberSet } from '../../actions/lumberSetActions';
import { useLumberListPrototypeAndSet } from '../../selectors/lumberListPrototypeSelectors';
import { UserVisibleError } from '../../UserVisibleError';
import { GRID_SPACING } from '../../util/constants';
import { useAppDispatch } from '../hooks';
import { EditCancelBar } from '../interface/controls/EditCancelBar';
import { LumberSetForm } from '../interface/forms/LumberSetForm';
import { formatLumber, LumberListPreview } from '../interface/LumberListPreview';
import { LumberListBreadcrumbs } from '../interface/navigation/LumberListBreadcrumbs';
import { paperSx } from '../interface/styles';
import { LoadingPage } from './LoadingPage';

export function LumberSetPage() {
  const params = useParams();
  const lumberListPrototypeAndSet = useLumberListPrototypeAndSet(params.lumberListGuid, params.lumberSetGuid);

  if (lumberListPrototypeAndSet == null) throw new UserVisibleError('Lumber Set and/or List could not be found');

  if (lumberListPrototypeAndSet === 'loading') return <LoadingPage />;

  const [lumberList, lumberSet] = lumberListPrototypeAndSet;

  return <UnconnectedLumberSetPage lumberList={lumberList} lumberSet={lumberSet} />;
}

interface Props {
  lumberList: LumberListPrototype;
  lumberSet: LumberSet;
}

export function UnconnectedLumberSetPage({ lumberList, lumberSet }: Props) {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const [changes, setChanges] = useState<Partial<LumberSet>>({});

  const updatedLumberSet = useMemo(() => ({ ...lumberSet!, ...changes }), [lumberSet, changes]);

  const handleLumberSetChanges = useCallback(
    (newChanges: Partial<LumberSet>) => {
      setChanges((oldChanges) => ({ ...oldChanges, ...newChanges }));
    },
    [setChanges],
  );

  const getLumberPreview = useCallback(async () => {
    return await designService.lumbers.expandLumberSet(updatedLumberSet);
  }, [updatedLumberSet]);

  const lumberPreviewQuery = usePromise(getLumberPreview);

  const lumberSetWithChanges = {
    ...lumberSet,
    ...changes,
  };

  return (
    <Container>
      <LumberListBreadcrumbs lumberList={lumberList} />

      <Grid container spacing={GRID_SPACING}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h3" gutterBottom>
            Editing Lumber Set
          </Typography>

          <Paper elevation={0} sx={paperSx}>
            <LumberSetForm value={lumberSetWithChanges} onChange={handleLumberSetChanges} />

            <EditCancelBar
              error={
                lumberPreviewQuery.data?.disallowedLumbers.length ? (
                  <Typography color="error">{`Not all lumbers have design values: ${formatLumber(
                    lumberPreviewQuery.data!.disallowedLumbers[0].lumber,
                  )}`}</Typography>
                ) : null
              }
              disabled={
                lumberPreviewQuery.isPending ||
                lumberPreviewQuery.data?.allowedLumbers?.length === 0 ||
                !!lumberPreviewQuery.data?.disallowedLumbers.length
              }
              onSubmit={async () => {
                await dispatch(upsertLumberSet(lumberList.guid, updatedLumberSet));
                navigate(`/lumberLists/${lumberList.guid}`);
              }}
            />
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <LumberListPreview
            lumbers={lumberPreviewQuery.data?.allowedLumbers}
            title="Lumber Preview"
            flavorText="These are the lumbers that match the conditions of this set.  You can't edit this list directly."
          />
        </Grid>
      </Grid>
    </Container>
  );
}
